import React, { useEffect, useRef, useState } from "react";
import { detectColors } from "../../services/products";
import { errorMessage } from "../../services";

// Clothing stores: the sizes and colours a shopper picks from on a product (the backend's utils/variants.js).
// Leaving both empty sells the product without a choice, as other store types do.
export const SIZE_PRESETS = {
  Women: ["S", "M", "L", "XL", "XXL"],
  "Plus size": ["3XL", "4XL", "5XL"],
  Kids: ["2-3Y", "4-5Y", "6-7Y", "8-9Y", "10-11Y"],
  "Free size": ["Free Size"],
};
// Gift stores: common options, under a name the owner picks ("Mug size", "Frame size")
export const GIFT_PRESETS = {
  Mug: ["11 oz", "15 oz"],
  Frame: ["6×8 in", "8×10 in", "A4"],
  Print: ["A4", "A3"],
  Pack: ["Small", "Medium", "Large"],
};
// Soap & perfume stores: common volumes and pack sizes
export const SELFCARE_PRESETS = {
  Perfume: ["15ml", "50ml", "100ml"],
  Soap: ["Single Bar", "Pack of 2", "Box of 3"],
  Bottle: ["100ml", "200ml", "500ml"],
  Jar: ["50g", "100g", "200g"],
};
// Every other store type: clothing-style sizes, or any size typed in (10 cm, 20mm, 100 inch)
// The presets are offered as quick chips with the price (PriceFields.js)
export const GENERAL_PRESETS = {
  Sizes: ["S", "M", "L", "XL", "XXL"],
  "Free size": ["Free Size"],
};
const QUICK_COLORS = [
  ["Black", "#1f1f1f"], ["White", "#f5f5f5"], ["Red", "#c0392b"], ["Maroon", "#7a1f2b"], ["Rani Pink", "#d6336c"], ["Peach", "#f6b99a"],
  ["Mustard", "#e1ad01"], ["Yellow", "#f2c94c"], ["Mint", "#63c5a5"], ["Green", "#3c8d5a"], ["Sky Blue", "#6fa8dc"], ["Navy", "#2c3e70"],
  ["Lavender", "#b39ddb"], ["Beige", "#d8c3a5"], ["Grey", "#9e9e9e"],
];
const MAX = 12;

// photo: the product's main photo. When it changes (a new upload or link), its garment colours are worked out on the
// server (services/colorDetect.js): the main one is put in the colour box, ready to add, and all of them are offered.
// gifts / selfcare: products whose sizes are named options ("Volume", "Pack size", "Mug size") with custom presets
// general: any other store type; the owner names the option ("Size", "Length") and types the sizes
// Only the colours: a product's sizes are set with its price (PriceFields.js)
export default function VariantFields({ colors = [], onChange, photo = "", siteId, gifts = false, selfcare = false }) {
  const isCustomOption = gifts || selfcare;
  const [colorName, setColorName] = useState("");
  const [colorHex, setColorHex] = useState("#d6336c");
  const [detected, setDetected] = useState(null); // null: not asked yet; [] : nothing found
  const [detecting, setDetecting] = useState(false);
  const [detectError, setDetectError] = useState("");
  const lastPhoto = useRef(photo);

  const detect = async (url = photo) => {
    if (!url || !siteId) return;
    setDetecting(true);
    setDetectError("");
    try {
      const found = await detectColors(siteId, url);
      setDetected(found);
      // The main colour goes in the colour box, unless it is already added (the others stay as chips to tap)
      const [first] = found;
      if (first && !colors.some((x) => x.name.toLowerCase() === first.name.toLowerCase())) {
        setColorName(first.name);
        setColorHex(first.hex);
      }
    } catch (err) {
      setDetected(null);
      setDetectError(errorMessage(err, "Could not read the colours of this photo."));
    } finally {
      setDetecting(false);
    }
  };

  // A new photo: detect its colours. Waits a moment so a link being typed is not read letter by letter.
  useEffect(() => {
    if (isCustomOption || !photo || photo === lastPhoto.current) return undefined;
    lastPhoto.current = photo;
    const timer = setTimeout(() => detect(photo), 600);
    return () => clearTimeout(timer);
    // detect reads the latest colours itself, so only a new photo runs it
  }, [photo, isCustomOption]);

  const hasColor = (name) => colors.some((c) => c.name.toLowerCase() === name.toLowerCase());
  const addColor = (name, hex) => {
    const clean = name.trim().slice(0, 24);
    if (!clean || hasColor(clean) || colors.length >= MAX) return;
    onChange({ colors: [...colors, { name: clean, hex }] });
  };
  const removeColor = (name) => onChange({ colors: colors.filter((c) => c.name !== name) });

  return (
    <div className="variant-fields">
      <div className="variant-block">
        <b>Colours <em>{colors.length ? `${colors.length} added` : "none: sold in one colour"}</em></b>
        {colors.length > 0 && (
          <div className="variant-chips">
            {colors.map((c) => (
              <button type="button" key={c.name} className="variant-chip on" onClick={() => removeColor(c.name)} aria-label={`Remove colour ${c.name}`}>
                <i style={{ background: c.hex || "#ddd" }} />{c.name} ×
              </button>
            ))}
          </div>
        )}
        {!isCustomOption && <div className="variant-detect">
          <button type="button" className="outline-btn" onClick={() => detect()} disabled={!photo || detecting}>
            {detecting ? "Looking at the photo…" : "✨ Detect colours from photo"}
          </button>
          {detectError && <small className="field-help variant-detect-error">{detectError}</small>}
          {!photo && <small className="field-help">Add the product photo first.</small>}
        </div>}
        {detected && (
          <div className="variant-suggest">
            <small>{detected.length ? "Found in the photo, tap to add:" : "No clear colour found. Pick one below."}</small>
            <div className="variant-chips">
              {detected.map((c) => (
                <button type="button" key={c.name} className={`variant-chip suggested ${hasColor(c.name) ? "on" : ""}`} disabled={hasColor(c.name)} onClick={() => addColor(c.name, c.hex)}>
                  <i style={{ background: c.hex }} />{c.name} <em>{Math.round(c.share * 100)}%</em>
                </button>
              ))}
            </div>
          </div>
        )}
        <small className="field-help">Tap to add a colour:</small>
        <div className="variant-chips">
          {QUICK_COLORS.filter(([name]) => !hasColor(name)).map(([name, hex]) => (
            <button type="button" key={name} className="variant-chip" onClick={() => addColor(name, hex)}><i style={{ background: hex }} />{name}</button>
          ))}
        </div>
        <div className="variant-add">
          <input type="color" value={colorHex} onChange={(e) => setColorHex(e.target.value)} aria-label="Colour" />
          <input className={detected && colorName && detected.some((c) => c.name === colorName) ? "is-suggested" : ""} value={colorName} onChange={(e) => setColorName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addColor(colorName, colorHex); setColorName(""); } }} placeholder="Colour name, e.g. Bottle Green" maxLength={24} />
          <button type="button" className="outline-btn" onClick={() => { addColor(colorName, colorHex); setColorName(""); }}>Add</button>
        </div>
      </div>
      <small className="field-help">Shoppers must pick a colour before adding this product to their cart, and you see it on the order. Leave empty to sell the product in one colour.</small>
    </div>
  );
}

// Gift stores: what the shopper adds to make the product theirs (product.personalization): a name or message box, and
// photos they upload (up to 3). "Required" stops the product being added without them.
export function PersonalizationFields({ value, onChange }) {
  const p = value || { text: false, textLabel: "", photos: 0, required: false };
  const set = (changes) => onChange({ ...p, ...changes });
  return (
    <div className="variant-fields personalization-fields">
      <b className="personalization-title">✍️ Personalisation <em>{p.text || p.photos ? "on" : "off"}</em></b>
      <label className="personalization-row"><input type="checkbox" checked={Boolean(p.text)} onChange={(e) => set({ text: e.target.checked })} /> Shopper adds a name or message</label>
      {p.text && (
        <label className="variant-option-name">Box label shown to shoppers
          <input value={p.textLabel || ""} maxLength={40} onChange={(e) => set({ textLabel: e.target.value })} placeholder="e.g. Name to print, Your message" />
        </label>
      )}
      <label className="variant-option-name">Photos the shopper uploads
        <select value={p.photos || 0} onChange={(e) => set({ photos: Number(e.target.value) })}>
          <option value={0}>None</option><option value={1}>1 photo</option><option value={2}>Up to 2 photos</option><option value={3}>Up to 3 photos</option>
        </select>
      </label>
      {(p.text || p.photos > 0) && (
        <label className="personalization-row"><input type="checkbox" checked={Boolean(p.required)} onChange={(e) => set({ required: e.target.checked })} /> Required: the product can't be added to the cart without them</label>
      )}
      <small className="field-help">You see the message and can open the photos on the order in Manage orders.</small>
    </div>
  );
}
